import { useEffect } from "react";

const SITE_URL = "https://www.carbonlabsau.com";
const DEFAULT_OG = `${SITE_URL}/brand/carbon-labs-logo.png`;

const setMeta = (name, content, attr = "name") => {
    if (!content) return;
    let el = document.querySelector(`meta[${attr}="${name}"]`);
    if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, name);
        document.head.appendChild(el);
    }
    el.setAttribute("content", content);
};

const setLink = (rel, href) => {
    let el = document.querySelector(`link[rel="${rel}"]`);
    if (!el) {
        el = document.createElement("link");
        el.setAttribute("rel", rel);
        document.head.appendChild(el);
    }
    el.setAttribute("href", href);
};

const setJsonLd = (id, data) => {
    let s = document.getElementById(id);
    if (s) s.remove();
    if (!data) return;
    s = document.createElement("script");
    s.id = id;
    s.type = "application/ld+json";
    s.text = JSON.stringify(data);
    document.head.appendChild(s);
};

/**
 * Comprehensive SEO tag injection for a route.
 *
 * @param {object} opts
 * @param {string} opts.title            — Page title (also og:title, twitter:title).
 * @param {string} opts.description      — Meta description (also og:description, twitter:description).
 * @param {string} [opts.image]          — Absolute URL to a preview image. Defaults to /brand/carbon-labs-logo.png.
 * @param {string} [opts.canonical]      — Absolute canonical URL. Defaults to SITE_URL + current pathname.
 * @param {string} [opts.keywords]       — Comma-separated keywords.
 * @param {string} [opts.type]           — og:type. Defaults to "website" (use "product" on product pages).
 * @param {object|object[]} [opts.jsonLd] — Structured data to inject.
 */
export const useSEO = ({
    title,
    description,
    image,
    canonical,
    keywords,
    type = "website",
    jsonLd,
}) => {
    useEffect(() => {
        const previousTitle = document.title;
        if (title) document.title = title;

        // Canonical — always emit; defaults to the current pathname on our root domain.
        const url = canonical || SITE_URL + (typeof window !== "undefined" ? window.location.pathname : "/");
        setLink("canonical", url);

        setMeta("description", description);
        if (keywords) setMeta("keywords", keywords);

        // Open Graph — Facebook, Messenger, Discord, LinkedIn all consume this.
        setMeta("og:site_name", "Carbon Labs Australia", "property");
        setMeta("og:title", title, "property");
        setMeta("og:description", description, "property");
        setMeta("og:type", type, "property");
        setMeta("og:url", url, "property");
        setMeta("og:image", image || DEFAULT_OG, "property");
        setMeta("og:locale", "en_AU", "property");

        // Twitter / X
        setMeta("twitter:card", image ? "summary_large_image" : "summary");
        setMeta("twitter:title", title);
        setMeta("twitter:description", description);
        setMeta("twitter:image", image || DEFAULT_OG);
        setMeta("twitter:site", "@carbonlabsau");

        // Structured data — accept a single object or an array of blocks.
        if (jsonLd) {
            if (Array.isArray(jsonLd)) {
                jsonLd.forEach((block, i) => setJsonLd(`seo-jsonld-${i}`, block));
            } else {
                setJsonLd("seo-jsonld", jsonLd);
            }
        }

        return () => {
            document.title = previousTitle;
            setJsonLd("seo-jsonld", null);
            for (let i = 0; i < 10; i++) setJsonLd(`seo-jsonld-${i}`, null);
        };
    }, [title, description, image, canonical, keywords, type, JSON.stringify(jsonLd)]);
};

export const SITE = SITE_URL;
